/**
 * Pure, DOM-light helpers for optional SDK behaviour driven by public config
 * (`bannerOptions` / `cookieCatalog`). Customer configs without these fields keep
 * the previous behaviour.
 */
import type {
  ConsentSdkCookieCatalog,
  ConsentSdkPurpose,
  ConsentSdkVisitorState,
} from "./consent-sdk.types.ts";

export type ResolvedThemeMode = "light" | "dark";

export function resolveThemeMode(
  mode: string | undefined,
  prefersDark: boolean,
): ResolvedThemeMode {
  if (mode === "dark") return "dark";
  if (mode === "auto") return prefersDark ? "dark" : "light";
  return "light";
}

export function normalizeVisitorState(v: unknown): ConsentSdkVisitorState {
  return v === "nothing" || v === "recommended" || v === "remember" || v === "necessary"
    ? v
    : "necessary";
}

/**
 * Before-choice state.
 *
 * - `nothing`: optional toggles off; no consent-gated script runs (not even required-tagged ones)
 *   until the visitor acts.
 * - `necessary`: optional toggles off; required-purpose scripts run immediately.
 * - `recommended`: optional purposes whose category/purpose is `defaultEnabled` are pre-toggled in
 *   the UI, but nothing optional runs until the visitor acts.
 * - `remember`: like `necessary`, plus the prior choice (any policy version) pre-fills toggles.
 */
export function initialVisitorGrants(opts: {
  purposes: ConsentSdkPurpose[];
  visitorState: ConsentSdkVisitorState;
  catalog?: ConsentSdkCookieCatalog | null;
  prior?: Record<string, boolean> | null;
}): { uiGrants: Record<string, boolean>; activeBeforeChoice: string[] } {
  const { purposes, visitorState, catalog, prior } = opts;
  const recommended = new Set<string>();
  for (const c of catalog?.categories ?? []) {
    if (c.purposeId && c.defaultEnabled) recommended.add(c.purposeId);
  }
  const uiGrants: Record<string, boolean> = {};
  for (const p of purposes) {
    if (p.required) {
      uiGrants[p.id] = true;
      continue;
    }
    if (visitorState === "recommended") {
      uiGrants[p.id] = recommended.has(p.id) || p.defaultEnabled === true;
    } else if (visitorState === "remember" && prior && prior[p.id] !== undefined) {
      uiGrants[p.id] = Boolean(prior[p.id]);
    } else {
      uiGrants[p.id] = false;
    }
  }
  const activeBeforeChoice =
    visitorState === "nothing" ? [] : purposes.filter((p) => p.required).map((p) => p.id);
  return { uiGrants, activeBeforeChoice };
}

/**
 * `remember`: a stored prior choice is reused across policy versions; the banner is only needed
 * again when the current policy has purposes the prior choice never covered.
 */
export function rememberNeedsPrompt(
  purposes: ConsentSdkPurpose[],
  prior: Record<string, boolean> | null | undefined,
): boolean {
  if (!prior) return true;
  return purposes.some((p) => !p.required && prior[p.id] === undefined);
}

/** Purpose ids a `data-aithoris-purpose` token refers to (purpose id or cookie category id). */
export function purposeIdsForToken(
  token: string,
  catalog?: ConsentSdkCookieCatalog | null,
): string[] {
  const t = token.trim();
  if (!t) return [];
  const out = new Set<string>([t]);
  for (const c of catalog?.categories ?? []) {
    if (c.id === t && c.purposeId) out.add(c.purposeId);
  }
  return [...out];
}

/** True when every purpose token on the element is granted. */
export function isGatedElementAllowed(
  attr: string,
  granted: Set<string>,
  catalog?: ConsentSdkCookieCatalog | null,
): boolean {
  const tokens = attr
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (!tokens.length) return false;
  return tokens.every((tok) => purposeIdsForToken(tok, catalog).some((id) => granted.has(id)));
}

/**
 * Activates `<script type="text/plain" data-aithoris-purpose="…">` for granted purposes.
 * Only consent-controlled (inert) scripts can be gated; scripts that already ran cannot be undone.
 */
export function activateGatedScripts(
  doc: Document,
  granted: Set<string>,
  catalog?: ConsentSdkCookieCatalog | null,
): number {
  let count = 0;
  const nodes = doc.querySelectorAll<HTMLScriptElement>(
    'script[type="text/plain"][data-aithoris-purpose]:not([data-aithoris-activated])',
  );
  nodes.forEach((node) => {
    const attr = node.getAttribute("data-aithoris-purpose") ?? "";
    if (!isGatedElementAllowed(attr, granted, catalog)) return;
    const next = doc.createElement("script");
    for (const { name, value } of Array.from(node.attributes)) {
      if (name === "type" || name === "data-aithoris-type") continue;
      next.setAttribute(name, value);
    }
    const realType = node.getAttribute("data-aithoris-type");
    if (realType) next.type = realType;
    if (!node.src && node.textContent) next.textContent = node.textContent;
    next.setAttribute("data-aithoris-activated", "true");
    node.setAttribute("data-aithoris-activated", "true");
    node.parentNode?.insertBefore(next, node.nextSibling);
    count += 1;
  });
  return count;
}

function domainCandidates(hostname: string, declared?: string | null): string[] {
  const out = new Set<string>([""]);
  const host = hostname.replace(/^\./, "").toLowerCase();
  if (host) {
    const parts = host.split(".");
    for (let i = 0; i < parts.length - 1; i++) {
      const d = parts.slice(i).join(".");
      out.add(d);
      out.add(`.${d}`);
    }
  }
  if (declared?.trim()) {
    const d = declared.trim().toLowerCase();
    const bare = d.replace(/^\./, "");
    // Browsers ignore Domain attributes that are not the host or one of its parents.
    if (host === bare || host.endsWith(`.${bare}`)) {
      out.add(bare);
      out.add(`.${bare}`);
    }
  }
  return [...out];
}

function pathCandidates(pathname: string, declared?: string | null): string[] {
  const out = new Set<string>(["/"]);
  if (declared?.trim()) out.add(declared.trim());
  const segs = pathname.split("/").filter(Boolean);
  let acc = "";
  for (const s of segs) {
    acc += `/${s}`;
    out.add(acc);
  }
  return [...out];
}

function matchCookieNames(pattern: string, existing: string[]): string[] {
  if (pattern.endsWith("*")) {
    const prefix = pattern.slice(0, -1);
    return existing.filter((n) => n.startsWith(prefix));
  }
  return existing.includes(pattern) ? [pattern] : [];
}

/**
 * `document.cookie` assignments that expire catalog cookies of denied purposes.
 * A cookie can only be removed with its original Path + Domain, so every plausible combination
 * for this page is emitted. HttpOnly and third-party cookies cannot be removed from script.
 */
export function cookieDeletionAssignments(opts: {
  catalog: ConsentSdkCookieCatalog | null | undefined;
  granted: Set<string>;
  existingCookieNames: string[];
  hostname: string;
  pathname: string;
}): string[] {
  const out: string[] = [];
  for (const cat of opts.catalog?.categories ?? []) {
    if (cat.required) continue;
    if (cat.purposeId && opts.granted.has(cat.purposeId)) continue;
    for (const ck of cat.cookies ?? []) {
      if (ck.httpOnly || !ck.name) continue;
      const names = matchCookieNames(ck.name, opts.existingCookieNames);
      for (const name of names) {
        for (const path of pathCandidates(opts.pathname, ck.path)) {
          for (const domain of domainCandidates(opts.hostname, ck.domain)) {
            out.push(
              `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=${path}${
                domain ? `; Domain=${domain}` : ""
              }`,
            );
          }
        }
      }
    }
  }
  return out;
}

export function existingCookieNames(cookieString: string): string[] {
  return cookieString
    .split(";")
    .map((c) => c.split("=")[0]?.trim() ?? "")
    .filter(Boolean);
}

export function deleteDeniedCookies(
  doc: Document,
  catalog: ConsentSdkCookieCatalog | null | undefined,
  granted: Set<string>,
): number {
  const assignments = cookieDeletionAssignments({
    catalog,
    granted,
    existingCookieNames: existingCookieNames(doc.cookie || ""),
    hostname: doc.location?.hostname ?? "",
    pathname: doc.location?.pathname ?? "/",
  });
  for (const a of assignments) doc.cookie = a;
  return assignments.length;
}

export function grantedSet(grants: Record<string, boolean> | null | undefined): Set<string> {
  return new Set(
    Object.entries(grants ?? {})
      .filter(([, v]) => v)
      .map(([k]) => k),
  );
}
